import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router';

import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useAdminSubmissions } from '@dashboard/hooks/queries/useAdminQueries';
import getSubmissionStatusLabel from '@dashboard/services/status/getSubmissionStatusLabel';
import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Checkbox, FormElement, Label } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';
import StatusBadge from '@dashboard/components/StatusBadge';
import ViewAsMerchantButton from '@dashboard/admin/components/ViewAsMerchantButton';

const Submissions = () => {
  const history = useHistory();

  const query = useUrlQuery();
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [livemode, setLivemode] = useState('livemode' in query ? query.livemode : true);
  const [status, setStatus] = useState(query.status || 'all');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        livemode,
        status,
      }),
    });
  }, [sortBy, sortDirection, livemode, status]);

  // Build filters object for the hook
  const filters = {
    sortBy,
    sortDirection,
    ...(livemode ? { livemode } : {}),
    ...(status !== 'all' ? { status } : {}),
    limit: 20,
  };

  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useAdminSubmissions(filters);

  // Convert sortBy/sortDirection to TanStack SortingState
  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  // Handle sorting changes from DataGrid
  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  // Flatten pages data for DataGrid
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Define columns for submissions table
  const columns = useMemo(
    () => [
      {
        accessorKey: 'page.title',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Form" />,
        cell: ({ row }) => {
          const submission = row.original;
          return (
            <div>
              <Link href={`/pages/${submission.page._id}`}>
                {submission.page.title || submission.page.name || 'Untitled'}
              </Link>
              {!submission.livemode && <StatusBadge status="test">Test</StatusBadge>}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'seller.displayName',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Merchant" />,
        cell: ({ row }) => {
          const submission = row.original;
          return (
            <div className="relative">
              <Link href={`/admin/merchants/${submission.seller?._id}`}>
                {submission.seller?.displayName || submission.seller?.name}
              </Link>
              {submission.seller?.disabled && (
                <Badge variant="secondary" className="ml-2 absolute right-0 mt-0.5">
                  Disabled
                </Badge>
              )}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
        cell: ({ row }) => {
          const createdAt = row.getValue('createdAt');
          return createdAt ? format(new Date(createdAt), 'd MMM, yyyy, HH:mm') : '';
        },
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }) => {
          const submission = row.original;
          return (
            <div className="space-x-2">
              <StatusBadge
                status={submission.status}
                label={getSubmissionStatusLabel(submission.status)}
              />
            </div>
          );
        },
        enableSorting: true,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <ViewAsMerchantButton
            sellerId={row.original.seller?._id}
            path={`/submissions/${row.original._id}`}
          >
            View submission
          </ViewAsMerchantButton>
        ),
        enableSorting: false,
        meta: { className: 'text-right' },
      },
    ],
    [],
  );

  // Filter controls
  const filterControls = (
    <div className="flex items-end justify-end gap-x-2">
      <FormElement className="!mb-0">
        <div className="flex items-center gap-x-2 w-full">
          <Checkbox
            id="livemode"
            value={!livemode}
            checked={!livemode}
            onChange={() => setLivemode(livemode ? false : true)}
          />

          <Label isInline htmlFor="livemode">
            Include test payments
          </Label>
        </div>
      </FormElement>

      <FormElement className="!mb-0">
        <Label>Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="succeeded">Paid</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>
    </div>
  );

  return (
    <div className="pr-4">
      <div className="mb-4">{filterControls}</div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={data?.pages?.[data.pages.length - 1]?.has_more}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No submissions found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default Submissions;
